import { FADE_DURATION_S } from "constants/pregnancyRevealReport";
import * as RevealIntroTextAnimationStyles from "./RevealIntroTextAnimation.styles";

// 줄바꿈 제외 글자 수
const getVisibleCharCount = (text: string) =>
  Array.from(text).filter((char) => char !== "\n").length;

// 짝수/홀수 웨이브 한 묶음 소요 시간
const getWaveDurationS = (count: number) => {
  if (count <= 0) return 0;
  return (
    Math.max(count - 1, 0) * RevealIntroTextAnimationStyles.CHAR_STAGGER_S +
    RevealIntroTextAnimationStyles.CHAR_DURATION_S
  );
};

// 짝수 → 홀수 블러 등장 총 소요 시간
const getBlockDurationS = (text: string) => {
  const visibleCount = getVisibleCharCount(text);
  const evenCount = Math.ceil(visibleCount / 2);
  const oddCount = Math.floor(visibleCount / 2);
  return getWaveDurationS(evenCount) + getWaveDurationS(oddCount);
};

// 짝수 → 홀수 순 블러 딜레이
const getCharDelayS = (
  visibleIndex: number,
  evenCount: number,
  baseDelayS: number,
) => {
  const orderInGroup = Math.floor(visibleIndex / 2);
  if (visibleIndex % 2 === 0) {
    return (
      baseDelayS + orderInGroup * RevealIntroTextAnimationStyles.CHAR_STAGGER_S
    );
  }
  return (
    baseDelayS +
    getWaveDurationS(evenCount) +
    orderInGroup * RevealIntroTextAnimationStyles.CHAR_STAGGER_S
  );
};

// 글자 단위 블러·오파시티 등장
const renderBlurChars = (text: string, baseDelayS: number) => {
  const chars = Array.from(text);
  const evenCount = Math.ceil(getVisibleCharCount(text) / 2);
  let visibleIndex = 0;

  return chars.map((char, keyIndex) => {
    if (char === "\n") {
      return <br key={`br-${keyIndex}`} />;
    }

    const delayS = getCharDelayS(visibleIndex, evenCount, baseDelayS);
    visibleIndex += 1;

    return (
      <RevealIntroTextAnimationStyles.StyledChar
        key={`char-${keyIndex}`}
        $delayS={delayS}
      >
        {char === " " ? "\u00A0" : char}
      </RevealIntroTextAnimationStyles.StyledChar>
    );
  });
};

// 띄어쓰기·줄바꿈 기준 분리 (마지막 제외 공백 포함)
const splitBodyTokens = (text: string) =>
  text.split("\n").map((line) => {
    const words = line
      .split(" ")
      .map((word) => word.trim())
      .filter((word) => word.length > 0);

    return words.map((word, index) =>
      index < words.length - 1 ? `${word} ` : word,
    );
  });

// 인트로 타이틀·본문 등장 애니메이션
const RevealIntroTextAnimation = ({
  dateTitle,
  mainTitle,
  bodyText,
  startDelayS = FADE_DURATION_S,
}: {
  dateTitle: string;
  mainTitle: string;
  bodyText: string;
  startDelayS?: number; // 상위 페이드 이후 시작 지연 (초)
}) => {
  const titleDurationS = Math.max(
    getBlockDurationS(dateTitle),
    getBlockDurationS(mainTitle),
  );
  const bodyDelayS =
    startDelayS +
    Math.max(RevealIntroTextAnimationStyles.LTR_DURATION_S, titleDurationS);
  const bodyLines = splitBodyTokens(bodyText);

  return (
    <RevealIntroTextAnimationStyles.StyledIntroTitleBox>
      <RevealIntroTextAnimationStyles.StyledLtrReveal $delayS={startDelayS}>
        <RevealIntroTextAnimationStyles.StyledDateTitle>
          {renderBlurChars(dateTitle, startDelayS)}
        </RevealIntroTextAnimationStyles.StyledDateTitle>
        <RevealIntroTextAnimationStyles.StyledMainTitle>
          {renderBlurChars(mainTitle, startDelayS)}
        </RevealIntroTextAnimationStyles.StyledMainTitle>
      </RevealIntroTextAnimationStyles.StyledLtrReveal>

      <RevealIntroTextAnimationStyles.StyledSubTitleBox>
        {bodyLines.map((words, lineIndex) => {
          const baseIndex = bodyLines
            .slice(0, lineIndex)
            .reduce((sum, line) => sum + line.length, 0);
          return (
            <RevealIntroTextAnimationStyles.StyledSubTitleLine
              key={`body-line-${lineIndex}`}
            >
              {words.map((word, wordIndex) => (
                <RevealIntroTextAnimationStyles.StyledWord
                  key={`body-word-${lineIndex}-${wordIndex}`}
                  $delayS={
                    bodyDelayS +
                    (baseIndex + wordIndex) *
                      RevealIntroTextAnimationStyles.WORD_STAGGER_S
                  }
                >
                  {word}
                </RevealIntroTextAnimationStyles.StyledWord>
              ))}
            </RevealIntroTextAnimationStyles.StyledSubTitleLine>
          );
        })}
      </RevealIntroTextAnimationStyles.StyledSubTitleBox>
    </RevealIntroTextAnimationStyles.StyledIntroTitleBox>
  );
};

export default RevealIntroTextAnimation;
